/**
 * ShippingWeightOverrideModal: 3PL client-catalog shipping weight (/products).
 *
 * Sets the 3PL's per-(sku, location) shipping weight, in kg per item, on a
 * CLIENT's SKUs at one of the caller's shared locations
 * (`inventory.setShippingWeightOverride`). A clone of `PrintTechOverrideModal`
 * (same props, chunking, partial-failure contract, single-location preselect)
 * with three differences:
 *  - Every selected SKU is sent. Weight is not a print concern, so there is no
 *    POD filter: pick-and-pack finished goods ship and need a weight too.
 *  - The value is a kg text field parsed by the shared `parseKg`, with the
 *    shared `kgFieldError` rule and Default shipping weights' messages (same
 *    bounds and 3-decimal guard there). kg only, no unit picker.
 *  - "Clear" deletes the 3PL weight so labels fall back to the client's own
 *    weight, then the client's default for the product type.
 *
 * The weight is a 3PL-only fact: it is never written to the client's Shopify
 * or to its catalog record, and product sync never changes it. That is the
 * thing the operator has to understand before submitting, so the body says so
 * rather than leaving it to a tooltip.
 *
 * Partial-failure contract: chunks are submitted sequentially, and a mid-loop
 * failure reports "applied N of M" in a toast that stays until dismissed, keeps
 * the modal open and the caller's selection intact (the write is absolute, so
 * re-submitting is idempotent), and calls `onPartialFailure` so the caller
 * refreshes the rows that did change (like `BulkDeleteProductsDialog`). After
 * any failed submit, focus returns to the weight field so Enter retries.
 *
 * Location: the caller's badge floor (`defaultLocationId`) is preselected when
 * it is one of the shared locations, else the only shared location. The weight
 * field takes focus on open once a location is known.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DEFAULT_WEIGHT_MAX_KG, DEFAULT_WEIGHT_MIN_KG } from '@threadheads/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatKg, kgFieldError, parseKg } from '@/lib/parse-kg';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

/** Router schema cap (`setShippingWeightOverrideSchema.skuIds` max 200). */
const SKU_CHUNK_SIZE = 200;

/** 'CLEAR' maps to a null `weightKg` (drop the 3PL weight at that location). */
type WeightMode = 'SET' | 'CLEAR';

interface ShippingWeightVariant {
  id: string;
}

interface ShippingWeightOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetOrganization: { id: string; name: string };
  /** Locations whose junction authorises this cross-org view. */
  sharedLocations: { id: string; name: string }[];
  variants: ShippingWeightVariant[];
  /**
   * The floor the catalog badges were resolved at (`listProductsGrouped`
   * `badgeLocationId`). Preselected when it is one of `sharedLocations`.
   */
  defaultLocationId?: string | null;
  /** Everything applied: the caller clears its selection and refreshes. */
  onUpdated: () => void | Promise<void>;
  /** Some chunks applied, then one failed: refresh WITHOUT clearing the
   *  selection, so submitting again finishes the rest. */
  onPartialFailure: () => void | Promise<void>;
}

export default function ShippingWeightOverrideModal({
  isOpen,
  onClose,
  targetOrganization,
  sharedLocations,
  variants,
  defaultLocationId = null,
  onUpdated,
  onPartialFailure,
}: ShippingWeightOverrideModalProps) {
  const { t } = useTranslation('inventory');
  const [pickedLocationId, setPickedLocationId] = useState<string | null>(null);
  const [mode, setMode] = useState<WeightMode>('SET');
  const [weightText, setWeightText] = useState('');
  // True once the field lost focus or took Enter after its last edit: a
  // half-typed prefix ("0.", "0.0") is only flagged then, or once no
  // continuation can be valid.
  const [weightCommitted, setWeightCommitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Bumped by every failed submit. Once the form is live again, focus goes
  // back to where a retry starts (DESIGN.md §12.2): the weight, so Enter
  // retries, or Apply when Clear hides the weight.
  const [failedSubmits, setFailedSubmits] = useState(0);
  const weightInputRef = useRef<HTMLInputElement>(null);
  const applyButtonRef = useRef<HTMLButtonElement>(null);

  const mutation = trpc.inventory.setShippingWeightOverride.useMutation();

  // Preselect the floor the operator is already looking at (the badges'
  // floor), else the only shared location. Derived, not copied into state, so
  // a list refresh while the modal is open never overrides a location the
  // operator picked.
  const preselectedLocationId = useMemo(() => {
    if (defaultLocationId && sharedLocations.some((l) => l.id === defaultLocationId)) {
      return defaultLocationId;
    }
    return sharedLocations.length === 1 ? (sharedLocations[0]?.id ?? null) : null;
  }, [defaultLocationId, sharedLocations]);
  const selectedLocation =
    sharedLocations.find((l) => l.id === (pickedLocationId ?? preselectedLocationId)) ?? null;
  const locationId = selectedLocation?.id ?? null;

  const skuIds = useMemo(() => variants.map((v) => v.id), [variants]);
  const parsed = parseKg(weightText);

  const noSharedLocations = sharedLocations.length === 0;
  const locationMissing = !locationId && !noSharedLocations;
  const weightError = mode === 'SET' ? kgFieldError(weightText, weightCommitted) : null;
  const weightReady = mode === 'CLEAR' || parsed.kind === 'ok';
  const canSubmit = !!locationId && skuIds.length > 0 && weightReady && !submitting;

  useEffect(() => {
    if (failedSubmits === 0) return;
    (weightInputRef.current ?? applyButtonRef.current)?.focus();
  }, [failedSubmits]);

  const handleClose = () => {
    setPickedLocationId(null);
    setMode('SET');
    setWeightText('');
    setWeightCommitted(false);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // `!locationId` is already in canSubmit; repeated here to narrow the type.
    if (!canSubmit || !locationId) return;

    const weightKg = mode === 'CLEAR' || parsed.kind !== 'ok' ? null : parsed.kg;
    // SKUs sent so far (the partial-failure toast), and rows written or
    // cleared (the success toast; a clear only counts SKUs that had a weight).
    let attempted = 0;
    let applied = 0;

    setSubmitting(true);
    try {
      for (let i = 0; i < skuIds.length; i += SKU_CHUNK_SIZE) {
        const chunk = skuIds.slice(i, i + SKU_CHUNK_SIZE);
        const result = await mutation.mutateAsync({
          locationId,
          targetOrganizationId: targetOrganization.id,
          skuIds: chunk,
          weightKg,
        });
        attempted += chunk.length;
        applied += result.count;
      }

      const location = selectedLocation?.name ?? '';
      if (weightKg === null) {
        // Nothing cleared (no selected SKU had a 3PL weight here) gets a
        // neutral title, not "cleared".
        const title =
          applied === 0
            ? t('clientCatalog.weightToast.nothingToClearTitle', { location })
            : t('clientCatalog.weightToast.clearedTitle', { location });
        toast.success(title, {
          // count 0 resolves `clearedCounts_zero` (i18next looks up `_zero`
          // first for count === 0, in every language).
          description: t('clientCatalog.weightToast.clearedCounts', { count: applied }),
        });
      } else {
        toast.success(t('clientCatalog.weightToast.setTitle', { location }), {
          description: t('clientCatalog.weightToast.setCounts', {
            count: applied,
            weight: formatKg(weightKg),
          }),
        });
      }
      handleClose();
      await onUpdated();
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : '';
      // Errors stay until dismissed (DESIGN.md §15.5).
      if (attempted > 0) {
        // Earlier chunks persisted. Keep the modal AND the caller's selection
        // so submitting again finishes the remainder (writes are absolute),
        // and let the caller refresh the rows that already changed.
        toast.error(
          t('clientCatalog.toast.partial', {
            applied: attempted,
            total: skuIds.length,
            message,
          }),
          { duration: Infinity },
        );
        // Not awaited: the refresh must not keep "Applying..." on the button
        // after the error toast is already up.
        void onPartialFailure();
      } else {
        toast.error(message || t('clientCatalog.weightToast.failed'), { duration: Infinity });
      }
      setFailedSubmits((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(event) => {
          // A location is already chosen, so the weight is the next thing to
          // type: start there instead of on the location picker.
          if (!locationId || mode !== 'SET') return;
          event.preventDefault();
          weightInputRef.current?.focus();
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {t('clientCatalog.weightModal.title', {
                count: variants.length,
                client: targetOrganization.name,
              })}
            </DialogTitle>
            <DialogDescription>
              {t('clientCatalog.weightModal.description', { client: targetOrganization.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {noSharedLocations ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t('clientCatalog.modal.noSharedLocations', { client: targetOrganization.name })}
              </p>
            ) : (
              <div className="grid gap-1.5">
                <Label htmlFor="shipping-weight-location" className="text-sm">
                  {t('clientCatalog.modal.locationLabel')}
                  <span aria-hidden="true" className="ml-0.5 text-destructive">
                    *
                  </span>
                </Label>
                <Select
                  value={locationId ?? undefined}
                  onValueChange={setPickedLocationId}
                  disabled={submitting}
                >
                  <SelectTrigger
                    id="shipping-weight-location"
                    aria-required
                    aria-invalid={locationMissing}
                    aria-describedby={locationMissing ? 'shipping-weight-location-helper' : undefined}
                    className={cn(
                      'transition-colors motion-reduce:transition-none',
                      locationMissing &&
                        'border-amber-300 ring-1 ring-amber-100/50 focus:ring-amber-200',
                    )}
                  >
                    <SelectValue placeholder={t('clientCatalog.modal.locationPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sharedLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locationMissing && (
                  <p id="shipping-weight-location-helper" className="text-xs text-amber-700">
                    {t('clientCatalog.modal.locationRequired')}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor="shipping-weight-mode" className="text-sm">
                {t('clientCatalog.weightModal.modeLabel')}
              </Label>
              <Select
                value={mode}
                onValueChange={(next) => setMode(next as WeightMode)}
                disabled={submitting || noSharedLocations}
              >
                <SelectTrigger id="shipping-weight-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SET">{t('clientCatalog.weightModal.modeSet')}</SelectItem>
                  <SelectItem value="CLEAR">
                    {t('clientCatalog.weightModal.modeClear', { client: targetOrganization.name })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mode === 'SET' && (
              <div className="grid gap-1.5">
                <Label htmlFor="shipping-weight-kg" className="text-sm">
                  {t('clientCatalog.weightModal.weightLabel')}
                  <span aria-hidden="true" className="ml-0.5 text-destructive">
                    *
                  </span>
                </Label>
                <Input
                  ref={weightInputRef}
                  id="shipping-weight-kg"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="0.2"
                  value={weightText}
                  onChange={(e) => {
                    setWeightText(e.target.value);
                    setWeightCommitted(false);
                  }}
                  onBlur={() => setWeightCommitted(true)}
                  // Enter commits the field like blur does, so a half-typed
                  // "0." shows its error instead of a silent disabled Apply.
                  onKeyDown={(e) => {
                    // An IME's Enter confirms a candidate; it is not a commit.
                    // Safari sends that Enter with isComposing false and
                    // keyCode 229, so check both.
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.key === 'Enter') setWeightCommitted(true);
                  }}
                  // readOnly, not disabled, while submitting: it blocks edits
                  // but keeps focus, so Enter can retry after a failure.
                  readOnly={submitting}
                  disabled={noSharedLocations}
                  aria-required
                  aria-invalid={weightError ? true : undefined}
                  aria-describedby={weightError ? 'shipping-weight-kg-helper' : 'shipping-weight-kg-note'}
                  className={cn(
                    'w-32 tabular-nums transition-colors motion-reduce:transition-none',
                    weightError && 'border-destructive',
                  )}
                />
                {/* One slot for helper-or-note: both share a grid cell, so the
                    cell keeps the note's height and an error swaps in over it
                    without moving anything below while the operator types. */}
                <div className="grid">
                  <p
                    id="shipping-weight-kg-note"
                    className={cn(
                      'col-start-1 row-start-1 text-xs text-muted-foreground',
                      weightError && 'invisible',
                    )}
                  >
                    {selectedLocation
                      ? t('clientCatalog.weightModal.note', {
                          location: selectedLocation.name,
                          client: targetOrganization.name,
                        })
                      : t('clientCatalog.weightModal.noteNoLocation', {
                          client: targetOrganization.name,
                        })}
                  </p>
                  <p
                    id="shipping-weight-kg-helper"
                    aria-live="polite"
                    className="col-start-1 row-start-1 text-xs text-destructive"
                  >
                    {weightError === 'tooPrecise'
                      ? t('settings:production.defaultWeights.tooPrecise')
                      : weightError === 'invalid'
                        ? t('settings:production.defaultWeights.validation', {
                            min: DEFAULT_WEIGHT_MIN_KG,
                            max: DEFAULT_WEIGHT_MAX_KG,
                          })
                        : null}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button ref={applyButtonRef} type="submit" disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('clientCatalog.modal.applying')}
                </>
              ) : (
                t('clientCatalog.modal.apply', { count: skuIds.length })
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
